import { useState } from 'react';
import { Heart, Plus, Star, Loader2, Check } from 'lucide-react';
import useWishlist from '../../hooks/useWishlist';
import { addToCart as apiAddToCart } from '../../utils/api';

const ACCENTS = ['accent1', 'accent2', 'accent3', 'secondary', 'primary'];

const ACCENT_GRAD = {
  accent1:   'from-accent1 to-pink-500',
  accent2:   'from-accent2 to-blue-500',
  accent3:   'from-accent3 to-green-500',
  secondary: 'from-secondary to-yellow-400',
  primary:   'from-primary to-primaryHover',
};

const ACCENT_BG = {
  accent1:   'from-accent1/10 to-pink-50',
  accent2:   'from-accent2/10 to-blue-50',
  accent3:   'from-accent3/10 to-green-50',
  secondary: 'from-secondary/10 to-yellow-50',
  primary:   'from-primary/10 to-purple-50',
};

const BORDER_COLOR = {
  accent1:   'border-accent1/10',
  accent2:   'border-accent2/10',
  accent3:   'border-accent3/10',
  secondary: 'border-secondary/10',
  primary:   'border-primary/10',
};

function StarRow({ rating = 0, compact = false }) {
  const cls = compact ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <div className={`flex items-center gap-0.5 text-secondary ${compact ? 'mb-1' : 'mb-3'}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${cls} ${n <= Math.round(rating) ? 'fill-current' : 'opacity-30'}`} />
      ))}
    </div>
  );
}

// Accepts either static props (emoji / price string) or a WooCommerce product object.
// compact=true → smaller card for 6-column grids
export default function ProductCard({
  name, emoji, price, originalPrice, rating = 0, reviewCount = 0, ageRange, badge, accent,
  product,
  index = 0,
  onAddToCart,
  compact = false,
}) {
  // Resolve data — must come before hooks
  const resolvedId       = product?.id;
  const resolvedName     = product?.name     ?? name    ?? 'Product';
  const resolvedPrice    = product ? parseFloat(product.price ?? 0).toFixed(2) : price;
  const _reg = parseFloat(product?.regular_price);
  const resolvedOriginal = product?.on_sale && !isNaN(_reg) && _reg > 0
    ? _reg.toFixed(2)
    : originalPrice;
  const resolvedRating   = product ? parseFloat(product.average_rating ?? 0)                : rating;
  const resolvedCount    = product?.rating_count ?? reviewCount;
  const resolvedImage    = product?.images?.[0]?.src ?? null;
  const resolvedUrl      = product?.permalink ?? '#';
  const resolvedBadge    = product?.on_sale ? '🏷️ SALE' : (product?.featured ? '⭐ FEATURED' : badge);
  const resolvedAccent   = accent ?? ACCENTS[index % ACCENTS.length];

  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const wishlisted = isWishlisted(resolvedId);

  const [adding, setAdding] = useState(false);
  const [added,  setAdded]  = useState(false);

  const grad   = ACCENT_GRAD[resolvedAccent]  ?? ACCENT_GRAD.accent1;
  const bg     = ACCENT_BG[resolvedAccent]    ?? ACCENT_BG.accent1;
  const border = BORDER_COLOR[resolvedAccent] ?? 'border-gray-100';

  async function handleAddToCart(e) {
    e.preventDefault();
    if (onAddToCart) { onAddToCart(resolvedId); return; }
    if (!resolvedId || adding) return;
    setAdding(true);
    try {
      await apiAddToCart(resolvedId, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch {
      if (resolvedUrl !== '#') window.location.href = resolvedUrl;
    } finally {
      setAdding(false);
    }
  }

  const cur = window.themeData?.currency ?? '$';

  // ── Compact card (6-column grid) ──────────────────────────────────────────
  if (compact) {
    return (
      <a
        href={resolvedUrl}
        className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border-2 ${border} block`}
      >
        <div className="relative">
          {resolvedBadge && (
            <div className={`absolute top-2 left-2 z-10 bg-gradient-to-r ${grad} text-white text-xs font-black px-2 py-1 rounded-full shadow`}>
              {resolvedBadge}
            </div>
          )}
          <button
            onClick={(e) => { e.preventDefault(); toggleWishlist(resolvedId); }}
            className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow transition-colors ${wishlisted ? 'bg-accent1 text-white' : 'bg-white text-accent1 hover:bg-accent1 hover:text-white'}`}
          >
            <Heart className={`w-3.5 h-3.5 ${wishlisted ? 'fill-current' : 'stroke-2'}`} />
          </button>
          <div className={`h-36 bg-gradient-to-br ${bg} flex items-center justify-center p-4 overflow-hidden group-hover:brightness-95 transition-all`}>
            {resolvedImage
              ? <img src={resolvedImage} alt={resolvedName} className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500" />
              : <div className="text-5xl group-hover:scale-125 transition-transform duration-500">{emoji ?? '🎁'}</div>
            }
          </div>
        </div>
        <div className="p-3">
          <StarRow rating={resolvedRating} compact />
          {resolvedCount > 0 && (
            <span className="text-gray-400 text-xs font-bold">({resolvedCount})</span>
          )}
          <h3 className="font-black text-textDark text-sm mb-0.5 leading-tight line-clamp-2">{resolvedName}</h3>
          {ageRange && <p className="text-textLight text-xs mb-1">{ageRange}</p>}
          <div className="flex items-center justify-between mt-1">
            <div>
              <span className="font-black text-base text-primary">{cur}{resolvedPrice}</span>
              {resolvedOriginal && resolvedOriginal !== resolvedPrice && (
                <span className="text-xs text-gray-400 line-through ml-1">{cur}{resolvedOriginal}</span>
              )}
            </div>
            <button
              onClick={handleAddToCart}
              disabled={adding}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all transform hover:scale-110 text-xs disabled:opacity-70 disabled:cursor-not-allowed ${added ? 'bg-accent3 text-white' : `bg-gradient-to-br ${grad} text-white hover:shadow-lg`}`}
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : added ? <Check className="w-3 h-3" /> : <Plus className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </a>
    );
  }

  // ── Regular card ─────────────────────────────────────────────────────────
  return (
    <a
      href={resolvedUrl}
      className={`group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-4 ${border} block`}
    >
      <div className="relative">
        {resolvedBadge && (
          <div className={`absolute top-4 left-4 z-10 bg-gradient-to-r ${grad} text-white text-xs font-black px-4 py-2 rounded-full shadow-lg`}>
            {resolvedBadge}
          </div>
        )}
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(resolvedId); }}
          className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-colors ${wishlisted ? 'bg-accent1 text-white' : 'bg-white text-accent1 hover:bg-accent1 hover:text-white'}`}
        >
          <Heart className={`w-4 h-4 ${wishlisted ? 'fill-current' : 'stroke-2'}`} />
        </button>
        <div className={`h-64 bg-gradient-to-br ${bg} flex items-center justify-center p-6 transition-all overflow-hidden`}>
          {resolvedImage
            ? <img src={resolvedImage} alt={resolvedName} className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500" />
            : <div className="text-9xl group-hover:scale-125 transition-transform duration-500">{emoji ?? '🎁'}</div>
          }
        </div>
      </div>
      <div className="p-6">
        <StarRow rating={resolvedRating} />
        {resolvedCount > 0 && (
          <span className="text-gray-500 text-sm font-bold -mt-2 mb-3 block">({resolvedCount})</span>
        )}
        <h3 className="font-black text-textDark text-xl mb-2 line-clamp-2">{resolvedName}</h3>
        {ageRange && <p className="text-textLight text-sm mb-4">{ageRange}</p>}
        <div className="flex items-center justify-between">
          <div>
            <span className="font-black text-3xl text-primary">{cur}{resolvedPrice}</span>
            {resolvedOriginal && resolvedOriginal !== resolvedPrice && (
              <span className="text-sm text-gray-400 line-through ml-2">{cur}{resolvedOriginal}</span>
            )}
          </div>
          <button
            onClick={handleAddToCart}
            disabled={adding}
            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all transform hover:scale-110 disabled:opacity-70 disabled:cursor-not-allowed ${added ? 'bg-accent3 text-white' : `bg-gradient-to-br ${grad} text-white hover:shadow-xl`}`}
          >
            {adding ? <Loader2 className="w-5 h-5 animate-spin" /> : added ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </a>
  );
}
